import { createApi } from '@reduxjs/toolkit/query/react';
import type { Dispatch } from '@reduxjs/toolkit';
import { waServicesQuery } from './baseQueries';
import { RecordResponse } from '../models/ServiceTypes';
import { advisorInvestorsApi } from './advisorInvestorsApi';
import { usersApi } from './usersApi';

export interface ClientRosterAssignment {
  AdvisorId: number;
  Name: string;
  IsManual: boolean;
}

export interface ClientRosterRecord {
  InvestorId: number;
  UserId: number;
  FirstName: string;
  LastName: string;
  UserName: string;
  GroupId: number;
  GroupName: string;
  PrimaryAdvisor: ClientRosterAssignment | null;
  SecondaryAdvisors: ClientRosterAssignment[];
}

export interface GetClientRosterArgs {
  searchTerm?: string;
  unassignedOnly?: boolean;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
  page?: number;
  pageSize?: number;
}

export interface ClientRosterEditRequest {
  FirstName: string;
  LastName: string;
  UserName: string;
  AdvisorGroupId: number;
  PrimaryAdvisorId: number | null;
  SecondaryAdvisorIds: number[];
}

export interface BulkPrimaryAdvisorRequest {
  InvestorIds: number[];
  AdvisorId: number;
}

export interface BulkSecondaryAdvisorsRequest {
  InvestorIds: number[];
  AdvisorIds: number[];
}

export interface ClientRosterBulkEditResult {
  UpdatedInvestorIds: number[];
  SkippedInvestorIds: number[];
}

type SecondaryAdvisorsBulkAction = 'add' | 'remove' | 'override';

function invalidateAdvisorInvestorCaches(
  dispatch: Dispatch,
  investorIds: number[],
  userIds?: number[],
): void {
  dispatch(
    advisorInvestorsApi.util.invalidateTags([
      { type: 'AdvisorInvestorsList', id: 'LIST' },
      { type: 'AdvisorSetupInvestors', id: 'LIST' },
      ...investorIds.map((id) => ({
        type: 'AdvisorInvestorById' as const,
        id,
      })),
    ]),
  );
  dispatch(
    usersApi.util.invalidateTags([
      { type: 'AdvisorInvestors', id: 'LIST' },
      ...investorIds.map((id) => ({
        type: 'AdvisorInvestors' as const,
        id: `INV_${String(id)}`,
      })),
      ...(userIds
        ? userIds.map((id) => ({ type: 'AdvisorInvestorSetup' as const, id }))
        : [{ type: 'AdvisorInvestorSetup' as const }]),
    ]),
  );
}

export const clientRelationshipsApi = createApi({
  reducerPath: 'clientRelationshipsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['ClientRoster'],
  endpoints: (builder) => {
    const secondaryAdvisorsBulkEndpoint = (
      action: SecondaryAdvisorsBulkAction,
    ) =>
      builder.mutation<
        ClientRosterBulkEditResult,
        BulkSecondaryAdvisorsRequest
      >({
        queryFn: async (body, { dispatch }, _extra, baseQuery) => {
          const result = await baseQuery({
            url: `/api/firm/client-roster/bulk/secondary-advisors/${action}`,
            method: 'POST',
            body,
          });
          if (result.error) return { error: result.error };
          invalidateAdvisorInvestorCaches(dispatch, body.InvestorIds);
          return { data: result.data as ClientRosterBulkEditResult };
        },
        invalidatesTags: () => [{ type: 'ClientRoster', id: 'LIST' }],
      });

    return {
      getClientRoster: builder.query<
        RecordResponse<ClientRosterRecord>,
        GetClientRosterArgs | undefined
      >({
        query: (args) => ({
          url: '/api/firm/client-roster',
          method: 'GET',
          params: {
            SearchTerm: args?.searchTerm,
            UnassignedOnly: args?.unassignedOnly,
            SortColumn: args?.sortColumn ?? 'LastName',
            SortDirection: args?.sortDirection ?? 'ASC',
            Page: args?.page ?? 1,
            PageSize: args?.pageSize ?? 50,
          },
        }),
        providesTags: () => [{ type: 'ClientRoster', id: 'LIST' }],
      }),
      updateClientRoster: builder.mutation<
        ClientRosterRecord,
        { investorId: number; body: ClientRosterEditRequest }
      >({
        queryFn: async (
          { investorId, body },
          { dispatch },
          _extra,
          baseQuery,
        ) => {
          const result = await baseQuery({
            url: `/api/firm/client-roster/${String(investorId)}`,
            method: 'PUT',
            body,
          });
          if (result.error) return { error: result.error };
          const record = result.data as ClientRosterRecord;
          invalidateAdvisorInvestorCaches(
            dispatch,
            [investorId],
            [record.UserId],
          );
          return { data: record };
        },
        invalidatesTags: () => [{ type: 'ClientRoster', id: 'LIST' }],
      }),
      bulkUpdatePrimaryAdvisor: builder.mutation<
        ClientRosterBulkEditResult,
        BulkPrimaryAdvisorRequest
      >({
        queryFn: async (body, { dispatch }, _extra, baseQuery) => {
          const result = await baseQuery({
            url: '/api/firm/client-roster/bulk/primary-advisor',
            method: 'POST',
            body,
          });
          if (result.error) return { error: result.error };
          invalidateAdvisorInvestorCaches(dispatch, body.InvestorIds);
          return { data: result.data as ClientRosterBulkEditResult };
        },
        invalidatesTags: () => [{ type: 'ClientRoster', id: 'LIST' }],
      }),
      bulkAddSecondaryAdvisors: secondaryAdvisorsBulkEndpoint('add'),
      bulkRemoveSecondaryAdvisors: secondaryAdvisorsBulkEndpoint('remove'),
      bulkOverrideSecondaryAdvisors: secondaryAdvisorsBulkEndpoint('override'),
    };
  },
});

export const {
  useGetClientRosterQuery,
  useLazyGetClientRosterQuery,
  useUpdateClientRosterMutation,
  useBulkUpdatePrimaryAdvisorMutation,
  useBulkAddSecondaryAdvisorsMutation,
  useBulkRemoveSecondaryAdvisorsMutation,
  useBulkOverrideSecondaryAdvisorsMutation,
} = clientRelationshipsApi;
